import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Where the Atelier keeps its projects: Supabase for the members (shared, versioned:
 * supabase/migrations/20261011090000_studio_projects.sql), or memory for the demo.
 * Images are files addressed by their sha256, uploaded once and never replaced.
 */
export interface ProjectSummary {
  id: string;
  name: string;
  template: string;
  version: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  slide_count: number;
}

export interface Project extends Omit<ProjectSummary, "slide_count"> {
  doc: unknown;
  archived_at: string | null;
}

export type StudioErrorCode = "conflict" | "not_found" | "archived" | "forbidden" | "invalid" | "network" | "unknown";

export class StudioError extends Error {
  constructor(
    readonly code: StudioErrorCode,
    message: string,
    /** For a conflict: the version now stored. */
    readonly remoteVersion?: number,
  ) {
    super(message);
    this.name = "StudioError";
  }
}

export interface StudioBackend {
  listProjects(): Promise<ProjectSummary[]>;
  getProject(id: string): Promise<Project>;
  createProject(name: string, doc: unknown): Promise<Project>;
  /** Refused with « conflict » when the stored version is no longer expectedVersion. */
  saveProject(id: string, expectedVersion: number, name: string, doc: unknown): Promise<Project>;
  archiveProject(id: string, expectedVersion: number): Promise<void>;
  /** Stores an image under its sha256; an image already there is left as it is. */
  putImage(sha256: string, data: Blob): Promise<void>;
  getImage(sha256: string): Promise<Blob>;
}

const SHA = /^[0-9a-f]{64}$/;

/** A database error code (SQLSTATE) as the Atelier explains it. */
export function studioError(sqlstate: string | undefined, message: string): StudioError {
  switch (sqlstate) {
    case "40001": {
      const version = Number(/version_conflict:\s*(\d+)/.exec(message)?.[1]);
      return new StudioError("conflict", "Ce projet vient d'être enregistré par ailleurs.", Number.isFinite(version) ? version : undefined);
    }
    case "P0002":
    case "22P02":
      return new StudioError("not_found", "Projet introuvable.");
    case "55000":
      return new StudioError("archived", "Ce projet a été archivé.");
    case "42501":
      return new StudioError("forbidden", "Accès réservé aux membres d'OPULANCIA.");
    case "23514":
      if (/doc_size/.test(message)) return new StudioError("invalid", "Projet trop volumineux pour être enregistré (512 Ko de texte au plus).");
      if (/no_inline_images/.test(message)) return new StudioError("invalid", "Le projet contient une image intégrée : les images doivent être envoyées comme fichiers.");
      return new StudioError("invalid", "Nom de projet invalide (1 à 120 caractères).");
    default:
      return new StudioError("unknown", message);
  }
}

interface ApiError {
  code?: string;
  message: string;
  status?: number;
  statusCode?: string | number;
}

const isNetwork = (message: string) => /fetch|network|load failed|timeout|abort|offline/i.test(message);

export class SupabaseStudioBackend implements StudioBackend {
  constructor(private readonly client: SupabaseClient) {}

  private async call<T>(run: () => PromiseLike<{ data: unknown; error: ApiError | null }>): Promise<T> {
    let result;
    try {
      result = await run();
    } catch (error) {
      throw new StudioError("network", error instanceof Error ? error.message : String(error));
    }
    if (result.error) {
      if (!result.error.code && isNetwork(result.error.message)) throw new StudioError("network", "Connexion interrompue.");
      throw studioError(result.error.code, result.error.message);
    }
    return result.data as T;
  }

  listProjects(): Promise<ProjectSummary[]> {
    return this.call(() => this.client.from("studio_project_list").select("*").order("updated_at", { ascending: false }));
  }

  async getProject(id: string): Promise<Project> {
    const rows = await this.call<Project[]>(() =>
      this.client.from("studio_projects").select("id, name, template, doc, version, created_by, updated_by, created_at, updated_at, archived_at").eq("id", id),
    );
    if (!rows[0]) throw new StudioError("not_found", "Projet introuvable.");
    return rows[0];
  }

  createProject(name: string, doc: unknown): Promise<Project> {
    return this.call(() => this.client.rpc("create_studio_project", { p_name: name, p_doc: doc }));
  }

  saveProject(id: string, expectedVersion: number, name: string, doc: unknown): Promise<Project> {
    return this.call(() => this.client.rpc("save_studio_project", { p_id: id, p_expected_version: expectedVersion, p_name: name, p_doc: doc }));
  }

  async archiveProject(id: string, expectedVersion: number): Promise<void> {
    await this.call(() => this.client.rpc("archive_studio_project", { p_id: id, p_expected_version: expectedVersion }));
  }

  async putImage(sha256: string, data: Blob): Promise<void> {
    if (!SHA.test(sha256)) throw new StudioError("invalid", "Empreinte d'image invalide.");
    let result;
    try {
      // upsert: false — a stored image is never replaced (the bucket has no update policy)
      result = await this.client.storage.from("studio").upload(`images/${sha256}`, data, { contentType: data.type, upsert: false, cacheControl: "31536000" });
    } catch (error) {
      throw new StudioError("network", error instanceof Error ? error.message : String(error));
    }
    if (!result.error) return;
    const error = result.error as ApiError;
    const status = Number(error.status ?? error.statusCode);
    if (status === 409 || /already exists|duplicate/i.test(error.message)) return;
    if (status === 403 || /row-level security|unauthorized/i.test(error.message)) throw new StudioError("forbidden", "Accès réservé aux membres d'OPULANCIA.");
    if (status === 413 || /too large|exceeded the maximum/i.test(error.message)) throw new StudioError("invalid", "Image plus lourde que la limite du stockage (15 Mo).");
    if (status === 415 || /mime type/i.test(error.message)) throw new StudioError("invalid", `Type d'image ${data.type || "inconnu"} refusé.`);
    if (!status || status >= 500 || isNetwork(error.message)) throw new StudioError("network", "Connexion interrompue pendant l'envoi de l'image.");
    throw new StudioError("unknown", error.message);
  }

  async getImage(sha256: string): Promise<Blob> {
    if (!SHA.test(sha256)) throw new StudioError("invalid", "Empreinte d'image invalide.");
    let result;
    try {
      result = await this.client.storage.from("studio").download(`images/${sha256}`);
    } catch (error) {
      throw new StudioError("network", error instanceof Error ? error.message : String(error));
    }
    if (result.error || !result.data) {
      const message = result.error?.message ?? "";
      throw isNetwork(message) ? new StudioError("network", "Connexion interrompue.") : new StudioError("not_found", "Image introuvable dans le stockage.");
    }
    return result.data;
  }
}

/**
 * The demo's store: FICTITIOUS, in this tab's memory, lost on reload. It applies the same
 * rules as the database (versions, archive, members) so the interface behaves the same.
 * `offline` simulates a lost connection.
 */
export class MemoryStudioBackend implements StudioBackend {
  offline = false;
  private readonly projects = new Map<string, Project>();
  private readonly images = new Map<string, Blob>();

  constructor(private readonly currentUser: () => string | null) {}

  private async ready(): Promise<string> {
    await new Promise((resolve) => setTimeout(resolve, 60));
    if (this.offline) throw new StudioError("network", "Connexion perdue (coupure simulée).");
    const user = this.currentUser();
    if (!user) throw new StudioError("forbidden", "Accès réservé aux membres d'OPULANCIA.");
    return user;
  }

  private check(name: string, doc: unknown): void {
    const text = JSON.stringify(doc);
    if (!doc || typeof doc !== "object" || Array.isArray(doc)) throw studioError("23514", "doc_object");
    if (new TextEncoder().encode(text).length > 524288) throw studioError("23514", "doc_size");
    if (text.includes("base64,")) throw studioError("23514", "no_inline_images");
    if (name.trim().length < 1 || name.trim().length > 120) throw studioError("23514", "name");
  }

  async listProjects(): Promise<ProjectSummary[]> {
    await this.ready();
    return [...this.projects.values()]
      .filter((p) => !p.archived_at)
      .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))
      .map(({ doc, archived_at: _a, ...p }) => ({ ...p, slide_count: Array.isArray((doc as { slides?: unknown[] }).slides) ? (doc as { slides: unknown[] }).slides.length : 0 }));
  }

  async getProject(id: string): Promise<Project> {
    await this.ready();
    const p = this.projects.get(id);
    if (!p) throw new StudioError("not_found", "Projet introuvable.");
    return structuredClone(p);
  }

  async createProject(name: string, doc: unknown): Promise<Project> {
    const user = await this.ready();
    this.check(name, doc);
    const now = new Date().toISOString();
    const p: Project = { id: crypto.randomUUID(), name: name.trim(), template: "liste", doc: structuredClone(doc), version: 1, created_by: user, updated_by: user, created_at: now, updated_at: now, archived_at: null };
    this.projects.set(p.id, p);
    return structuredClone(p);
  }

  async saveProject(id: string, expectedVersion: number, name: string, doc: unknown): Promise<Project> {
    const user = await this.ready();
    const p = this.projects.get(id);
    if (!p) throw new StudioError("not_found", "Projet introuvable.");
    if (p.archived_at) throw studioError("55000", "project_archived");
    if (p.version !== expectedVersion) throw studioError("40001", `version_conflict: ${p.version}`);
    this.check(name, doc);
    Object.assign(p, { name: name.trim(), doc: structuredClone(doc), version: p.version + 1, updated_by: user, updated_at: new Date().toISOString() });
    return structuredClone(p);
  }

  async archiveProject(id: string, expectedVersion: number): Promise<void> {
    const user = await this.ready();
    const p = this.projects.get(id);
    if (!p) throw new StudioError("not_found", "Projet introuvable.");
    if (p.version !== expectedVersion) throw studioError("40001", `version_conflict: ${p.version}`);
    Object.assign(p, { archived_at: p.archived_at ?? new Date().toISOString(), version: p.version + 1, updated_by: user, updated_at: new Date().toISOString() });
  }

  async putImage(sha256: string, data: Blob): Promise<void> {
    await this.ready();
    if (!SHA.test(sha256)) throw new StudioError("invalid", "Empreinte d'image invalide.");
    if (!this.images.has(sha256)) this.images.set(sha256, data);
  }

  async getImage(sha256: string): Promise<Blob> {
    await this.ready();
    const blob = this.images.get(sha256);
    if (!blob) throw new StudioError("not_found", "Image introuvable dans le stockage.");
    return blob;
  }
}
